import { useEffect, useMemo } from 'react'

// @ts-expect-error TS7016
import { ArtworkDownloadContext, BuildingArtworkModal, BuildingImageModal } from '@domain-group/artwork-builder'

import type { UseFiltersPayload } from '@domain-group/react-hooks'
import { useModals, useSetState, useShowModal } from '@domain-group/react-hooks'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ApproveOrderModal from '@modals/ApproveOrderModal'
import CanvaArtworkingLoadingModal from '@modals/CanvaArtworkingLoadingModal/CanvaArtworkingLoadingModal'
import ArtworkBuilderSelectionModal from '@modals/ArtworkBuilderSelectionModal/ArtworkBuilderSelectionModal'
import BulkShareArtworksModal from '@modals/BulkShareArtworksModal'
import CanvaTemplateSelectionModal from '@modals/CanvaTemplateSelectionModal/CanvaTemplateSelectionModal'
import CreateCreditNoteAndDeleteOrderModal from '@modals/CreateCreditNoteAndDeleteOrderModal'
import CreateOrEditOrderItemTaskModal from '@modals/CreateOrEditOrderItemTaskModal'
import DuplicateOrderItemArtworkModal from '@modals/DuplicateOrderItemArtworkModal'
import EditItemOptionsModal from '@modals/EditItemOptionsModal'
import EditOrderTotalModal from '@modals/EditOrderTotalModal'
import GenerateCampaignScheduleModal from '@modals/GenerateCampaignScheduleModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import ManageOrderItemModal from '@modals/ManageOrderItemModal'
import ManageOrderItemTasksModal from '@modals/ManageOrderItemTasksModal'
import NewOrderModal from '@modals/NewOrderModal'
import SelectOrderItemLiftoverModal from '@modals/SelectOrderItemLiftoverModal'
import SelectPublicationEditionModal from '@modals/SelectPublicationEditionModal'
import UploadArtworkModal from '@modals/UploadArtworkModal'

import type {
  ActivityModel,
  CampaignModel,
  OrderItemModel,
  OrderItemTaskModel,
  OrderModel,
} from '@models/types'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    closeCanvaArtworkingLoadingModal: () => void,
    showApproveOrderModal: () => void,
    showArtworkBuilderSelectionModal: () => void,
    showBulkShareArtworksModal: () => void,
    showCanvaTemplateSelectionModal: () => void,
    showCreateCreditNoteAndDeleteOrderModal: () => void,
    showCreateOrEditOrderItemTaskModal: () => void,
    showDuplicateOrderItemArtworkModal: () => void,
    showEditItemOptionsModal: () => void,
    showEditOrderTotalModal: () => void,
    showGenerateCampaignScheduleModal: () => void,
    showManageOrderItemModal: () => void,
    showManageOrderItemTasksModal: () => void,
    showNewOrderModal: () => void,
    showSelectOrderItemLiftoverModal: () => void,
    showSelectPublicationEditionModal: () => void,
    showUploadArtworkModal: () => void,
  },
}

const defaultState = {
  showCanvaArtworkingLoadingModal: false,
  showApproveOrderModal: false,
  showArtworkBuilderSelectionModal: false,
  showBulkShareArtworksModal: false,
  showCreateCreditNoteAndDeleteOrderModal: false,
  showCreateOrEditOrderItemTaskModal: false,
  showDuplicateOrderItemArtworkModal: false,
  showEditItemOptionsModal: false,
  showEditOrderTotalModal: false,
  showGenerateCampaignScheduleModal: false,
  showManageActivityModal: false,
  showManageOrderItemModal: false,
  showManageOrderItemTasksModal: false,
  showNewOrderModal: false,
  showSelectOrderItemLiftoverModal: false,
  showSelectPublicationEditionModal: false,
  showCanvaTemplateSelectionModal: false,
  showUploadArtworkModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CanvaArtworkingLoadingModal: {
      closeModal: () => setState({ showCanvaArtworkingLoadingModal: false }),
    },
    ApproveOrderModal: {
      approveOrder: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showApproveOrderModal: false }),
    },
    ArtworkBuilderSelectionModal: {
      closeModal: () => setState({ showArtworkBuilderSelectionModal: false }),
    },
    BulkShareArtworksModal: {
      bulkShareArtworks: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showBulkShareArtworksModal: false }),
    },
    CanvaTemplateSelectionModal: {
      closeModal: () => setState({ showCanvaTemplateSelectionModal: false }),
    },
    CreateCreditNoteAndDeleteOrderModal: {
      closeModal: () => setState({ showCreateCreditNoteAndDeleteOrderModal: false }),
    },
    CreateOrEditOrderItemTaskModal: {
      closeModal: () => setState({ showCreateOrEditOrderItemTaskModal: false }),
      completeOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    DuplicateOrderItemArtworkModal: {
      closeModal: () => setState({ showDuplicateOrderItemArtworkModal: false }),
      selectExistingArtwork: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditItemOptionsModal: {
      closeModal: () => setState({ showEditItemOptionsModal: false }),
      updateItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditOrderTotalModal: {
      closeModal: () => setState({ showEditOrderTotalModal: false }),
      updateOrder: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    GenerateCampaignScheduleModal: {
      closeModal: () => setState({ showGenerateCampaignScheduleModal: false }),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    ManageOrderItemModal: {
      closeModal: () => setState({ showManageOrderItemModal: false }),
      duplicateOrderItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      reorderItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateOrderItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageOrderItemTasksModal: {
      closeModal: () => setState({ showManageOrderItemTasksModal: false }),
    },
    NewOrderModal: {
      addItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showNewOrderModal: false }),
    },
    SelectOrderItemLiftoverModal: {
      closeModal: () => setState({ showSelectOrderItemLiftoverModal: false }),
      selectLiftover: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    SelectPublicationEditionModal: {
      closeModal: () => setState({ showSelectPublicationEditionModal: false }),
    },
    UploadArtworkModal: {
      closeModal: () => setState({ showUploadArtworkModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

type PendingOrdersProps = {
  campaign: CampaignModel,
}

const PendingOrders = (props: PendingOrdersProps) => {
  const { campaign } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showCanvaArtworkingLoadingModal,
    showApproveOrderModal,
    showArtworkBuilderSelectionModal,
    showBulkShareArtworksModal,
    showCreateCreditNoteAndDeleteOrderModal,
    showCreateOrEditOrderItemTaskModal,
    showDuplicateOrderItemArtworkModal,
    showEditItemOptionsModal,
    showEditOrderTotalModal,
    showGenerateCampaignScheduleModal,
    showManageActivityModal,
    showCanvaTemplateSelectionModal,
    showManageOrderItemModal,
    showManageOrderItemTasksModal,
    showNewOrderModal,
    showSelectOrderItemLiftoverModal,
    showSelectPublicationEditionModal,
    showUploadArtworkModal,
  } = state

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData }, topModalKey } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      closeCanvaArtworkingLoadingModal: () => {
        setState({ showCanvaArtworkingLoadingModal: false })
      },
      showCanvaArtworkingLoadingModal: (payload: { paragraph?: string, title?: string }) => {
        setModalData('CanvaArtworkingLoadingModal', payload)
        setState({ showCanvaArtworkingLoadingModal: true })
      },
      showApproveOrderModal: (payload: { order: OrderModel }) => {
        setModalData('ApproveOrderModal', payload)
        setState({ showApproveOrderModal: true })
      },
      showArtworkBuilderSelectionModal: (payload: { orderItem: OrderItemModel }) => {
        setModalData('ArtworkBuilderSelectionModal', payload)
        setState({ showArtworkBuilderSelectionModal: true })
      },
      showBulkShareArtworksModal: () => {
        setState({ showBulkShareArtworksModal: true })
      },
      showCanvaTemplateSelectionModal: (payload: { orderItem: OrderItemModel }) => {
        setModalData('CanvaTemplateSelectionModal', payload)
        setState({ showCanvaTemplateSelectionModal: true })
      },
      showCreateCreditNoteAndDeleteOrderModal: (payload: { order?: OrderModel }) => {
        setModalData('CreateCreditNoteAndDeleteOrderModal', payload)
        setState({ showCreateCreditNoteAndDeleteOrderModal: true })
      },
      showCreateOrEditOrderItemTaskModal: (payload: { orderItem: OrderItemTaskModel }) => {
        setModalData('CreateOrEditOrderItemTaskModal', payload)
        setState({ showCreateOrEditOrderItemTaskModal: true })
      },
      showDuplicateOrderItemArtworkModal: (payload: { order: OrderModel }) => {
        setModalData('DuplicateOrderItemArtworkModal', payload)
        setState({ showDuplicateOrderItemArtworkModal: true })
      },
      showEditItemOptionsModal: (payload: { orderItem: OrderItemModel }) => {
        setModalData('EditItemOptionsModal', payload)
        setState({ showEditItemOptionsModal: true })
      },
      showEditOrderTotalModal: (payload: { order: OrderModel }) => {
        setModalData('EditOrderTotalModal', payload)
        setState({ showEditOrderTotalModal: true })
      },
      showGenerateCampaignScheduleModal: () => {
        setState({ showGenerateCampaignScheduleModal: true })
      },
      showManageActivityModal: (payload: { orderItem: ActivityModel }) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showManageOrderItemModal: (payload: { orderItem: OrderItemModel }) => {
        setModalData('ManageOrderItemModal', payload)
        setState({ showManageOrderItemModal: true })
      },
      showManageOrderItemTasksModal: (payload: { orderItem: OrderItemModel }) => {
        setModalData('ManageOrderItemTasksModal', payload)
        setState({ showManageOrderItemTasksModal: true })
      },
      showNewOrderModal: (payload: { agencyId: number, campaignId: number }) => {
        setModalData('NewOrderModal', payload)
        setState({ showNewOrderModal: true })
      },
      showSelectOrderItemLiftoverModal: (payload: { orderItem: OrderItemModel }) => {
        setModalData('SelectOrderItemLiftoverModal', payload)
        setState({ showSelectOrderItemLiftoverModal: true })
      },

      showSelectPublicationEditionModal: (payload: { orderItem: OrderItemModel }) => {
        setModalData('SelectPublicationEditionModal', payload)
        setState({ showSelectPublicationEditionModal: true })
      },
      showUploadArtworkModal: (payload: { orderItem: OrderItemModel }) => {
        setModalData('UploadArtworkModal', payload)
        setState({ showUploadArtworkModal: true })
      },
    },
  }), [modalContext, topModalKey]) // modalContext dependency required if layering modals

  useShowModal({
    modalKey: 'ManageOrderItemModal',
    options: {
      // @ts-expect-error TS2322
      callbacks: pageContext.callbacks,
    },
  })

  useShowModal({
    modalKey: 'CreateOrEditOrderItemTaskModal',
    options: {
      // @ts-expect-error TS2322
      callbacks: pageContext.callbacks,
    },
  })

  useShowModal({
    modalKey: 'NewOrderModal',
    options: {
      // @ts-expect-error TS2322
      callbacks: pageContext.callbacks,
    },
  })

  const { order_item_id, order_item_task_id } = parsePermittedQueryParams(getQueryParamsFromHash(), [
    'order_item_id', 'order_item_task_id',
  ])

  useEffect(() => {
    if (order_item_id){
      setModalData('ManageOrderItemModal', { orderItem: { id: order_item_id } })
      setState({ showManageOrderItemModal: true })
    }
    if (order_item_task_id){
      setModalData('CreateOrEditOrderItemTaskModal', { orderItemTask: { id: order_item_task_id } })
      setState({ showCreateOrEditOrderItemTaskModal: true })
    }
  }, [order_item_id, order_item_task_id])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent campaign={campaign} />

        <CanvaArtworkingLoadingModal
          callbacks={callbacks('CanvaArtworkingLoadingModal', setState)}
          showModal={showCanvaArtworkingLoadingModal}
        />

        <ApproveOrderModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ApproveOrderModal', setState)}
          showModal={showApproveOrderModal}
        />

        <ArtworkBuilderSelectionModal
          callbacks={callbacks('ArtworkBuilderSelectionModal', setState)}
          showModal={showArtworkBuilderSelectionModal}
        />

        <BuildingArtworkModal context={ArtworkDownloadContext} />

        <BuildingImageModal context={ArtworkDownloadContext} />

        <BulkShareArtworksModal
          // @ts-expect-error TS2322
          callbacks={callbacks('BulkShareArtworksModal', setState)}
          campaign={campaign}
          showModal={showBulkShareArtworksModal}
        />

        <CanvaTemplateSelectionModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CanvaTemplateSelectionModal', setState)}
          showModal={showCanvaTemplateSelectionModal}
        />

        <CreateCreditNoteAndDeleteOrderModal
          callbacks={callbacks('CreateCreditNoteAndDeleteOrderModal', setState)}
          showModal={showCreateCreditNoteAndDeleteOrderModal}
        />

        <CreateOrEditOrderItemTaskModal
          allowedTaskTypes={['clean', 'move', 'other', 'remove', 'repair']}
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditOrderItemTaskModal', setState)}
          showModal={showCreateOrEditOrderItemTaskModal}
        />

        <DuplicateOrderItemArtworkModal
          // @ts-expect-error TS2322
          callbacks={callbacks('DuplicateOrderItemArtworkModal', setState)}
          showModal={showDuplicateOrderItemArtworkModal}
          requestOptions={{
            ...defaultRequestOptions.artwork,
            ...defaultRequestOptions.artworkPage,
            ...defaultRequestOptions.orderItem,
          }}
        />

        <EditItemOptionsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditItemOptionsModal', setState)}
          showModal={showEditItemOptionsModal}
          requestOptions={{
            ...defaultRequestOptions.orderItem,
          }}
        />

        <EditOrderTotalModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditOrderTotalModal', setState)}
          showModal={showEditOrderTotalModal}
          requestOptions={{
            ...defaultRequestOptions.order,
          }}
        />

        <GenerateCampaignScheduleModal
          callbacks={callbacks('GenerateCampaignScheduleModal', setState)}
          campaign={campaign}
          showModal={showGenerateCampaignScheduleModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <ManageOrderItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageOrderItemModal', setState)}
          showModal={showManageOrderItemModal}
        />

        <ManageOrderItemTasksModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageOrderItemTasksModal', setState)}
          showModal={showManageOrderItemTasksModal}
        />

        <NewOrderModal
          callbacks={callbacks('NewOrderModal', setState)}
          showModal={showNewOrderModal}
        />

        <SelectOrderItemLiftoverModal
          // @ts-expect-error TS2322
          callbacks={callbacks('SelectOrderItemLiftoverModal', setState)}
          showModal={showSelectOrderItemLiftoverModal}
          requestOptions={{
            ...defaultRequestOptions.order,
            ...defaultRequestOptions.orderItem,
            ...defaultRequestOptions.publication,
            ...defaultRequestOptions.publicationEdition,
          }}
        />

        <SelectPublicationEditionModal
          callbacks={callbacks('SelectPublicationEditionModal', setState)}
          showModal={showSelectPublicationEditionModal}
        />

        <UploadArtworkModal
          callbacks={callbacks('UploadArtworkModal', setState)}
          showModal={showUploadArtworkModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default PendingOrders
